import { useTranslation } from "react-i18next";
import { SUPPORTED } from "@/i18n";

/**
 * Single source of truth for "what language is the user in?".
 *
 * Exposes:
 *   t(key)                  — translation function (react-i18next pass-through)
 *   code                    — current 2-letter language code, e.g. "hi"
 *   meta                    — { code, name, name_native, rtl, ai_instruction }
 *   languages               — full registry [{code, name_native, ...}]
 *   setLanguage(code)       — switches the active language app-wide
 *   aiInstruction()         — system-prompt clause to inject into LLM calls
 *
 * Reuses i18next's internal store so any t() call elsewhere updates
 * synchronously. localStorage persistence happens inside i18n/index.js's
 * languageChanged handler.
 */
export default function useLanguage() {
    const { t, i18n } = useTranslation();
    const code = i18n.language || "en";

    function meta(forCode) {
        const c = forCode || code;
        const bundle = i18n.getResourceBundle(c, "translation") || {};
        return bundle.$meta || { code: c };
    }

    function setLanguage(next) {
        if (!SUPPORTED.some((s) => s.code === next)) return;
        i18n.changeLanguage(next);
    }

    function aiInstruction() {
        // The full instruction is authored per-language in $meta.ai_instruction
        // so a contributor adding a new locale just writes one good sentence
        // in the native script — no JavaScript edits.
        return meta(code).ai_instruction || "";
    }

    return {
        t,
        code,
        meta: meta(code),
        languages: SUPPORTED.map((s) => meta(s.code)),
        setLanguage,
        aiInstruction,
    };
}
